import { formatDate, formatMoney, useTranslation } from "@roastery/i18n";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "@roastery/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { usePortalFormatSettings } from "../lib/use-portal-format";
import { useIsWide } from "../lib/use-media-query";
import { usePortalAccount, usePortalInvoices, type PortalInvoice } from "../lib/portal-api";
import { InvoiceStatusBadge } from "../components/invoice-status-badge";
import { InvoicePdfButton } from "../components/invoice-pdf-button";

export const Route = createFileRoute("/_authed/invoices/")({
  component: InvoicesPage,
});

/**
 * Portal → Invoices (US-25.4): what this café owes and what it has been billed for.
 *
 * <p>The balance sits above the table rather than beside the last row, because it is the answer to
 * the question that brought the buyer here. The list underneath is the working out.
 */
function InvoicesPage() {
  const { t } = useTranslation();
  const formats = usePortalFormatSettings();
  const [unsettledOnly, setUnsettledOnly] = useState(true);

  const account = usePortalAccount();
  const invoices = usePortalInvoices(unsettledOnly);
  const rows: PortalInvoice[] = invoices.data?.content ?? [];
  const wide = useIsWide();

  const pdfOf = (invoice: PortalInvoice) =>
    invoice.pdfHere ? (
      <InvoicePdfButton invoiceId={invoice.id ?? ""} number={invoice.number ?? ""} />
    ) : invoice.pdfUrl ? (
      <Button asChild variant="outline" size="sm">
        <a href={invoice.pdfUrl} target="_blank" rel="noreferrer" data-testid={`portal-invoice-pdf-${invoice.id}`}>
          {t("portal.invoices.pdf")}
        </a>
      </Button>
    ) : (
      <Badge variant="muted">{t("portal.invoices.noPdf")}</Badge>
    );

  return (
    <div className="flex flex-col gap-4" data-testid="portal-invoices-page">
      <PageHeader title={t("portal.invoices.title")} description={t("portal.invoices.description")} />
      <Card>
        <CardContent className="flex flex-wrap gap-4" data-testid="portal-account-balance">
          {(account.data?.balances ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("portal.invoices.nothingOwed")}</p>
          ) : (
            (account.data?.balances ?? []).map((balance) => (
              <div key={balance.currency} className="rounded-md border border-border p-3">
                <div className="text-xs text-muted-foreground">
                  {t("portal.invoices.outstanding", { currency: balance.currency })}
                </div>
                <div className="text-lg font-semibold tabular-nums">
                  {formatMoney(balance.outstanding ?? 0, formats, balance.currency ?? "PLN")}
                </div>
                {balance.overdue && balance.overdue > 0 ? (
                  <div className="mt-1 text-xs font-medium text-destructive">
                    {t("portal.invoices.overdue", {
                      amount: formatMoney(balance.overdue, formats, balance.currency ?? "PLN"),
                    })}
                  </div>
                ) : null}
              </div>
            ))
          )}
          {(account.data?.overpayments ?? []).map((spare) => (
            <div key={`over-${spare.currency}`} className="rounded-md border border-dashed border-border p-3">
              <div className="text-xs text-muted-foreground">{t("portal.invoices.overpaid")}</div>
              <div className="text-lg font-semibold tabular-nums">
                {formatMoney(spare.outstanding ?? 0, formats, spare.currency ?? "PLN")}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          actions={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="portal-invoices-filter"
              onClick={() => setUnsettledOnly((current) => !current)}
            >
              {unsettledOnly ? t("portal.invoices.showAll") : t("portal.invoices.showUnsettled")}
            </Button>
          }
        >
          <CardTitle className="text-base">{t("portal.invoices.list")}</CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.isLoading ? (
            <TableSkeleton rows={5} label={t("common.loading")} />
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="portal-invoices-empty">
              {t("portal.invoices.empty")}
            </p>
          ) : wide ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("portal.invoices.columns.number")}</TableHead>
                  <TableHead>{t("portal.invoices.columns.issuedOn")}</TableHead>
                  <TableHead>{t("portal.invoices.columns.dueOn")}</TableHead>
                  <TableHead className="text-right">{t("portal.invoices.columns.total")}</TableHead>
                  <TableHead className="text-right">{t("portal.invoices.columns.balance")}</TableHead>
                  <TableHead>{t("portal.invoices.columns.status")}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((invoice) => (
                  <TableRow key={invoice.id} data-testid={`portal-invoice-row-${invoice.id}`}>
                    <TableCell className="font-mono text-sm">
                      {invoice.number}
                      {invoice.orderNumbers ? (
                        <div className="text-xs text-muted-foreground">{invoice.orderNumbers}</div>
                      ) : null}
                    </TableCell>
                    <TableCell>{invoice.issuedOn ? formatDate(invoice.issuedOn, formats) : "—"}</TableCell>
                    <TableCell>
                      {invoice.dueOn ? formatDate(invoice.dueOn, formats) : "—"}
                      {invoice.daysOverdue ? (
                        <span className="ml-2 text-xs font-medium text-destructive">
                          {t("portal.invoices.daysLate", { count: invoice.daysOverdue })}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney(invoice.totalGross ?? 0, formats, invoice.currency ?? "PLN")}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {formatMoney(invoice.balance ?? 0, formats, invoice.currency ?? "PLN")}
                    </TableCell>
                    <TableCell>
                      <InvoiceStatusBadge status={invoice.status} />
                    </TableCell>
                    <TableCell className="text-right">{pdfOf(invoice)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            // On a phone: number, due date and what is left to pay, then the document. The issue
            // date and the gross total are on the PDF; seven columns did not fit.
            <ul className="flex flex-col gap-3">
              {rows.map((invoice) => (
                <li
                  key={invoice.id}
                  className="flex flex-col gap-2 rounded-md border border-border p-3"
                  data-testid={`portal-invoice-row-${invoice.id}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-mono text-sm font-medium whitespace-nowrap">{invoice.number}</span>
                    <InvoiceStatusBadge status={invoice.status} />
                  </div>
                  <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                    <span className="text-muted-foreground">
                      {t("portal.invoices.columns.dueOn")}: {invoice.dueOn ? formatDate(invoice.dueOn, formats) : "—"}
                      {invoice.daysOverdue ? (
                        <span className="ml-2 text-xs font-medium text-destructive">
                          {t("portal.invoices.daysLate", { count: invoice.daysOverdue })}
                        </span>
                      ) : null}
                    </span>
                    <span className="font-medium tabular-nums">
                      {formatMoney(invoice.balance ?? 0, formats, invoice.currency ?? "PLN")}
                    </span>
                  </div>
                  {invoice.orderNumbers ? (
                    <div className="text-xs text-muted-foreground">{invoice.orderNumbers}</div>
                  ) : null}
                  <div>{pdfOf(invoice)}</div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
